/**
 * Content Store Module
 */

// Dependencies
import { firebaseAction } from 'vuexfire'
import fb from '@/lib/fb'
import path from 'path'

// Types
import * as types from '../types'

// State
const state = {
	content: [],
	item: {},
	page: {
		current: 1,
		limit: 50,
		count: 1,
	},
	search: {
		keyword: null,
		keys: [],
		sort: 'createdAt',
	},
	sort: {
		field: 'createdAt',
		reverse: true,
	},
}

// Actions
const actions = {
	loadContent: firebaseAction(({ rootState, bindFirebaseRef, commit }, key) => {
		commit(types.SET_LOADING, true)
		const contentPath = path.join(rootState.user.refs.content, key)
		const contentRef = fb.database().ref(contentPath)

		return new Promise((resolve, reject) => {
			bindFirebaseRef('content', contentRef, { readyCallback: () => {
				commit(types.SET_LOADING, false)
				resolve()
			} })
		})
	}),
	unloadContent: firebaseAction(({ commit, unbindFirebaseRef }) => {
		commit(types.UNLOAD_CONTENT)

		try {
			unbindFirebaseRef('content')
		} catch (e) {}
	}),
	loadItem: firebaseAction(({ rootState, bindFirebaseRef }, ref) => {
		const itemPath = path.join(rootState.user.refs.content, ref)
		const itemRef = fb.database().ref(itemPath)

		return new Promise((resolve, reject) => {
			bindFirebaseRef('item', itemRef, { readyCallback: () => {
				resolve()
			} })
		})
	}),
	unloadItem: firebaseAction(({ commit, unbindFirebaseRef }, ref) => {
		try {
			unbindFirebaseRef('item')
		} catch (e) {}
	}),
	loadContentAlt: firebaseAction(({ rootState, bindFirebaseRef }, key) => {
		const contentPath = path.join(rootState.user.refs.content, key)
		const contentRef = fb.database().ref(contentPath)

		return new Promise((resolve, reject) => {
			bindFirebaseRef('contentAlt', contentRef, { readyCallback: () => {
				resolve()
			} })
		})
	}),
	unloadContentAlt: firebaseAction(({ commit, unbindFirebaseRef }) => {
		commit(types.UNLOAD_CONTENT)
		try {
			unbindFirebaseRef('contentAlt')
		} catch (e) {}
	}),
	loadContentLocal: ({ rootState, commit }, key) => {
		const contentPath = path.join(rootState.user.refs.content, key)
		const contentRef = fb.database().ref(contentPath)

		return new Promise((resolve, reject) => {
			contentRef.once('value').then(snap => {
				resolve(fb.prepOut(snap, true))
			})
		})
	},
	loadItemLocal: ({ rootState, commit }, key) => {
		const itemPath = path.join(rootState.user.refs.content, key)
		const itemRef = fb.database().ref(itemPath)

		return new Promise((resolve, reject) => {
			itemRef.once('value').then(snap => {
				resolve(fb.prepOut(snap))
			})
		})
	},
	addItem: ({ commit, rootState }, ref) => {
		const contentPath = path.join(rootState.user.refs.content, ref)
		const contentRef = fb.database().ref(contentPath)
		const item = fb.prepIn({})
		const newRef = contentRef.push()
		const key = newRef.key

		return new Promise((resolve, reject) => {
			newRef.set(item, res => {
				resolve(key)
			})
		})
	},
	removeItem: ({ commit }, data) => {
		const ref = fb.ref(data)

		return fb.database().ref(ref).remove()
	},
	updateItem: ({ commit, rootState }, data) => {
		const ref = fb.ref(data)
		const item = fb.prepIn(data)
		const key = fb.key(data)
		const contentRef = fb.database().ref(ref)

		return new Promise((resolve, reject) => {
			contentRef.update(item, res => {
				resolve(key)
			})
		})
	},
	updateItemPriority: ({ commit, rootState }, data) => {
		const ref = fb.ref(data)
		const item = fb.prepIn(data, false)
		const key = fb.key(data)
		const contentRef = fb.database().ref(ref)

		return new Promise((resolve, reject) => {
			contentRef.update(item, res => {
				resolve(key)
			})
		})
	},
	uploadImage: ({ commit }, data) => {
		const key = Object.keys(data)[0]
		const val = data[key]
		const storageRef = fb.storage().ref(key)
		const img = new Image()
		const _url = window.URL || window.webkitURL

		let height = 0
		let width = 0
		const dimensions = {
			height: 0,
			width: 0,
		}
		img.onload = function () {
			dimensions.height = this.height
			dimensions.width = this.width
			height = this.height
			width = this.width
		}
		img.src = _url.createObjectURL(val)

		return new Promise((resolve, reject) => {
			const upload = storageRef.put(val)
			upload.on(
				fb.storage.TaskEvent.STATE_CHANGED,
				snap => {
				},
				err => {
					reject(err)
				},
				res => {
					const toReturn = {
						downloadURL: upload.snapshot.downloadURL,
						contentType: upload.snapshot.metadata.contentType,
						fullPath: upload.snapshot.metadata.fullPath,
						height,
						name: upload.snapshot.metadata.name,
						size: upload.snapshot.metadata.size,
						width,
					}
					resolve(toReturn)
				})
		})
	},
	uploadFile: ({ commit }, data) => {
		const key = Object.keys(data)[0]
		const val = data[key]
		const storageRef = fb.storage().ref(key)

		return new Promise((resolve, reject) => {
			const upload = storageRef.put(val)
			upload.on(
				fb.storage.TaskEvent.STATE_CHANGED,
				snap => {
				},
				err => {
					reject(err)
				},
				res => {
					const toReturn = {
						downloadURL: upload.snapshot.downloadURL,
						contentType: upload.snapshot.metadata.contentType,
						size: upload.snapshot.metadata.size,
						fullPath: upload.snapshot.metadata.fullPath,
						name: upload.snapshot.metadata.name,
					}
					resolve(toReturn)
				})
		})
	},
	setPage: ({ commit }, page) => {
		commit(types.SET_PAGE, parseInt(page, 10))
	},
	setSearch: ({ commit }, data) => {
		if (typeof data.keyword !== 'string') return
		commit(types.SET_SEARCH, data)
	},
	setSortField: ({ commit }, data) => {
		commit(types.SET_SORT_FIELD, data)
	},
	appendContent: ({ commit, rootState }, data) => {
		const key = Object.keys(data)[0]
		const val = data[key]
		const contentPath = path.join(rootState.user.refs.content, key)
		const contentRef = fb.database().ref(contentPath)
		return new Promise((resolve, reject) => {
			contentRef.update(val, err => {
				if (err) {
					reject(err)
				} else {
					resolve(val)
				}
			})
		})
	},
	replaceContent: ({ commit, rootState }, data) => {
		const key = Object.keys(data)[0]
		const val = data[key]
		const contentPath = path.join(rootState.user.refs.content, key)
		const contentRef = fb.database().ref(contentPath)
		return new Promise((resolve, reject) => {
			contentRef.set(val, err => {
				if (err) {
					reject(err)
				} else {
					resolve(val)
				}
			})
		})
	},
}

// Mutations
const mutations = {
	[types.UNLOAD_CONTENT](state) {
		state.content = []
	},
	[types.UNLOAD_ITEM](state) {
		state.item = {}
	},
	[types.SET_LOADING](state, loading) {
		state.loading = loading
	},
	[types.SET_PAGE](state, page) {
		state.page.current = page
	},
	[types.SET_PAGE_LIMIT](state, limit) {
		state.page.limit = limit
	},
	[types.SET_SEARCH](state, search) {
		state.search.keys = search.keys
		if (search.keyword.length < 3) {
			state.search.keyword = ''
		} else {
			state.search.keyword = search.keyword
		}
	},
	[types.SET_SORT_FIELD](state, field) {
		if (state.sort.field === field) {
			state.sort.reverse = !state.sort.reverse
		} else {
			state.sort.field = field
		}
	},
}

// Getters
const getters = {
	contents: state => state.contents,
	content: state => {
		if (!state.content.length) return state.content

		const filtered = state.content.filter(item => {
			for (const key of state.search.keys) {
				return (item[key] || '').toLowerCase().indexOf(
					state.search.keyword.toLowerCase()
				) > -1
			}
			return item
		})

		const clean = str => {
			if (typeof str === 'number') return str
			return String(str).replace(/\W/g, '').toLowerCase()
		}

		const current = state.page.current
		filtered.sort((a, b) => {
			if (clean(a[state.sort.field]) < clean(b[state.sort.field])) return state.sort.reverse ? 1 : -1
			if (clean(a[state.sort.field]) > clean(b[state.sort.field])) return state.sort.reverse ? -1 : 1
			return 0
		})

		state.page.count = Math.ceil(filtered.length / state.page.limit)
		const content = []
		const page = Math.max(0, current - 1)
		const start = (page * state.page.limit)
		const limit = (start + state.page.limit)
		for (let i = start; i < limit; i++) {
			if (filtered[i]) {
				content.push(filtered[i])
			}
		}

		return content
	},
	allContent: state => state.content,
	item: state => state.item,
	page: state => state.page,
	sort: state => state.sort,
}

export default {
	state,
	actions,
	mutations,
	getters,
}



// WEBPACK FOOTER //
// ./src/lib/store/modules/content.js